import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  WifiOff,
  Wifi,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";
import { useOnline } from "../hooks/useOnline.jsx";
import { useStore } from "../store/index.js";
import { apiFetch } from "../api/core.js";

/**
 * Bannière offline avancée :
 * - Amber si hors-ligne : liste dépliable des actions en queue
 * - Bouton "Synchroniser maintenant"
 * - Verte au retour online + sync auto + toast par action
 */
export default function OfflineBanner() {
  const online = useOnline();
  const offlineQueue = useStore((s) => s.offlineQueue);
  const removeFromOfflineQueue = useStore((s) => s.removeFromOfflineQueue);
  const [expanded, setExpanded] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResults, setSyncResults] = useState([]); // { id, label, ok, error }
  const [showSuccess, setShowSuccess] = useState(false);
  const prevOnline = useRef(online);

  // Sync auto au retour online
  useEffect(() => {
    if (!prevOnline.current && online && offlineQueue.length > 0) {
      replayQueue();
    }
    prevOnline.current = online;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [online]);

  // Écouter les messages du Service Worker (background sync)
  useEffect(() => {
    function handleSWMessage(event) {
      if (event.data?.type === "FEDIS_SYNC_TRIGGER" && offlineQueue.length > 0) {
        replayQueue();
      }
    }
    navigator.serviceWorker?.addEventListener("message", handleSWMessage);
    return () => navigator.serviceWorker?.removeEventListener("message", handleSWMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offlineQueue]);

  const replayQueue = useCallback(async () => {
    if (syncing || offlineQueue.length === 0) return;
    setSyncing(true);
    setSyncResults([]);

    const results = [];
    for (const action of offlineQueue) {
      try {
        await apiFetch(action.url, {
          method: action.method,
          body: action.body,
        });
        removeFromOfflineQueue(action.id);
        results.push({ id: action.id, label: action.label, ok: true });
      } catch (err) {
        // Ne pas retirer de la queue si l'erreur n'est pas OFFLINE_QUEUED (sinon on perd l'action)
        if (err.code !== "OFFLINE_QUEUED") {
          results.push({ id: action.id, label: action.label, ok: false, error: err.message });
        }
      }
    }

    setSyncResults(results);
    setSyncing(false);

    if (results.every((r) => r.ok)) {
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    }
  }, [offlineQueue, removeFromOfflineQueue, syncing]);

  function formatTime(iso) {
    try {
      return new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
    } catch {
      return "";
    }
  }

  // Rien à afficher si online + queue vide + pas de résultats
  if (online && offlineQueue.length === 0 && !showSuccess && syncResults.length === 0) {
    return null;
  }

  // Bannière verte : sync OK
  if (online && showSuccess) {
    return (
      <div className="flex items-center gap-2 bg-emerald-600 px-4 py-2 text-sm text-white">
        <CheckCircle2 className="h-4 w-4 shrink-0" />
        <span>Synchronisation réussie — toutes les actions ont été envoyées.</span>
      </div>
    );
  }

  // Bannière verte : résultats partiels / erreurs
  if (online && syncResults.length > 0 && !showSuccess) {
    const failed = syncResults.filter((r) => !r.ok);
    return (
      <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 text-sm text-amber-800">
        <div className="flex items-center gap-2">
          <AlertCircle className="h-4 w-4 shrink-0 text-amber-600" />
          <span>{failed.length} action(s) n'ont pas pu être synchronisées.</span>
          <button
            onClick={() => setSyncResults([])}
            className="ml-auto text-xs underline"
          >
            Fermer
          </button>
        </div>
        <ul className="mt-1 space-y-0.5 text-xs">
          {failed.map((r) => (
            <li key={r.id} className="text-rose-700">
              {r.label} — {r.error}
            </li>
          ))}
        </ul>
      </div>
    );
  }

  // Bannière verte : retour online, queue en attente (avant sync auto)
  if (online && offlineQueue.length > 0) {
    return (
      <div className="flex items-center gap-2 bg-sky-600 px-4 py-2 text-sm text-white">
        <Wifi className="h-4 w-4 shrink-0" />
        <span>Reconnecté — synchronisation de {offlineQueue.length} action(s)…</span>
        {syncing && <RefreshCw className="h-4 w-4 animate-spin" />}
      </div>
    );
  }

  // Bannière amber : mode hors-ligne
  return (
    <div className="bg-amber-500 text-white text-sm shadow-sm">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-2">
        <WifiOff className="h-4 w-4 shrink-0" />
        <span className="font-medium">
          Mode hors-ligne
          {offlineQueue.length > 0 && (
            <> — <span className="font-bold">{offlineQueue.length}</span> action(s) en attente</>
          )}
        </span>
        <div className="ml-auto flex items-center gap-2">
          {offlineQueue.length > 0 && (
            <button
              onClick={() => setExpanded((v) => !v)}
              className="inline-flex items-center gap-1 rounded-full bg-amber-400 px-2.5 py-1 text-xs font-semibold hover:bg-amber-300"
            >
              Détails
              {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            </button>
          )}
        </div>
      </div>

      {/* Liste dépliable */}
      {expanded && offlineQueue.length > 0 && (
        <div className="border-t border-amber-400 bg-amber-600 px-4 py-3">
          <div className="space-y-1.5">
            {offlineQueue.map((action) => (
              <div
                key={action.id}
                className="flex items-center justify-between rounded-lg bg-amber-500 px-3 py-1.5 text-xs"
              >
                <span className="font-medium">{action.label}</span>
                <span className="text-amber-200">{formatTime(action.queuedAt)}</span>
              </div>
            ))}
          </div>
          <button
            onClick={replayQueue}
            disabled={syncing || !online}
            className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-white px-4 py-2 text-xs font-bold text-amber-700 hover:bg-amber-50 disabled:opacity-50"
          >
            {syncing ? (
              <><RefreshCw className="h-3.5 w-3.5 animate-spin" /> Synchronisation…</>
            ) : (
              <><RefreshCw className="h-3.5 w-3.5" /> Synchroniser maintenant</>
            )}
          </button>
          {!online && (
            <p className="mt-2 text-center text-xs text-amber-200">
              Reconnectez-vous pour synchroniser.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
